import { VideoInfo, EFFECT_DEFINITIONS } from './types';
import {configs} from '../../resources/configs';
import {getAccessToken} from '../../utils/AuthUtill';

const API_BASE_URL = configs.apiUrl;

function getAuthHeaders(): Record<string, string> {
  const token = getAccessToken() || '';
  return {
    'authorization': token,
    'content-type': 'application/json',
  };
}

export async function getUploadUrl(filename: string, contentType: string = 'video/mp4'): Promise<{ uploadUrl: string; videoId: string; s3Key: string; s3Url: string }> {
  const response = await fetch(`${API_BASE_URL}/template-auto/video/upload-url`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ filename, contentType }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get upload URL');
  }

  return response.json();
}

export async function uploadToS3(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type || 'video/mp4',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to upload file to S3');
  }
}

export async function processVideo(s3Url: string, filename: string, threshold: number = 27.0): Promise<VideoInfo> {
  const response = await fetch(`${API_BASE_URL}/template-auto/video/process`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ s3Url, filename, threshold }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to process video');
  }

  return response.json();
}

export async function getFilmstripUrl(videoId: string): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/template-auto/video/${videoId}/filmstrip`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get filmstrip URL');
  }

  const data = await response.json();
  return data.filmstripUrl;
}

export async function getVideoStreamUrl(videoId: string): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/template-auto/video/${videoId}/stream-url`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get video stream URL');
  }

  const data = await response.json();
  return data.streamUrl;
}

export async function exportTimings(
  videoId: string,
  markers: number[],
  duration: number,
  effectMarkers: any[]
): Promise<string> {
  // Get category for each effect from definitions using effectId
  const effectMarkersWithCategory = effectMarkers.map(em => {
    const def = EFFECT_DEFINITIONS.find(d => d.id === em.effectId);
    return {
      effect_code: em.effectCode,  // Use full code like B1, O2, etc.
      category: def?.category || em.category || 'clip',
      time: em.time,
      params: em.params,
    };
  });

  const response = await fetch(`${API_BASE_URL}/template-auto/video/${videoId}/export`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      markers,
      video_duration: duration,
      effect_markers: effectMarkersWithCategory,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to export timings');
  }

  return response.text();
}
